// 机器列表（/api/machines）。

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { apiGet, apiPostForm, APIError } from "@/api/client";
import { API_MACHINES, machineAuthMetadataPath } from "@/api/endpoints";
import type { MachineListResponse, MachineListEntry } from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { StatusBadge, type StatusVariant } from "@/components/ui/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { InlineEditableCell } from "@/components/forms/InlineEditableCell";
import { AuthActionsCell } from "@/components/forms/AuthActionsCell";
import { AuthDeleteButton } from "@/components/forms/AuthDeleteButton";
import { toast } from "@/components/ui/Sonner";
import { useSession } from "@/hooks/useSession";
import { formatMs } from "@/lib/format";

export interface MachinesSearch {
  status?: string;
  q?: string;
  cursor?: number;
}

const AUTH_STATUS_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "pending", label: "待审批" },
  { value: "verified", label: "已通过" },
  { value: "disabled", label: "已禁用" },
  { value: "revoked", label: "已吊销" },
] as const;
const AUTH_STATUS_VALUES: ReadonlySet<string> = new Set(
  AUTH_STATUS_OPTIONS.filter((option) => option.value !== "all").map(
    (option) => option.value,
  ),
);

function parseMachinesSearch(search: Record<string, unknown>): MachinesSearch {
  const pick = (v: unknown): string | undefined => {
    if (typeof v !== "string") return undefined;
    const t = v.trim();
    return t === "" ? undefined : t;
  };
  const status = pick(search.status);
  let cursor: number | undefined;
  if (search.cursor !== undefined && search.cursor !== null && search.cursor !== "") {
    const n = Number(search.cursor);
    if (Number.isFinite(n) && n >= 0) cursor = Math.trunc(n);
  }
  return {
    status: status && AUTH_STATUS_VALUES.has(status) ? status : undefined,
    q: pick(search.q),
    cursor,
  };
}

export const Route = createFileRoute("/machines/")({
  validateSearch: parseMachinesSearch,
  component: MachinesIndexRoute,
});

function MachinesIndexRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/machines/" });
  return (
    <MachinesView
      search={search}
      onChange={(next) => navigate({ search: next, replace: false })}
    />
  );
}

function buildMachinesQuery(search: MachinesSearch): string {
  const params = new URLSearchParams();
  if (search.status) params.set("status", search.status);
  if (search.q) params.set("q", search.q);
  if (typeof search.cursor === "number") {
    params.set("cursor", String(search.cursor));
  }
  return params.toString();
}

function statusToVariant(status: string): StatusVariant {
  switch (status) {
    case "verified":
      return "ok";
    case "pending":
      return "warn";
    case "revoked":
      return "danger";
    default:
      return "neutral";
  }
}

interface MachinesViewProps {
  search: MachinesSearch;
  onChange: (next: MachinesSearch) => void;
}

export function MachinesView({ search, onChange }: MachinesViewProps) {
  const queryClient = useQueryClient();
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const actor = session.data?.actor ?? "anonymous";
  const [form, setForm] = useState<MachinesSearch>(search);

  const q = useQuery({
    queryKey: ["machines", search],
    queryFn: () =>
      apiGet<MachineListResponse>(`${API_MACHINES}?${buildMachinesQuery(search)}`),
  });

  function apply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onChange({ ...form, cursor: undefined });
  }
  function reset() {
    setForm({});
    onChange({});
  }

  async function saveMetadata(authID: number, note: string, tags: string) {
    try {
      await apiPostForm(machineAuthMetadataPath(authID), { note, tags });
      toast.success("已保存");
      void queryClient.invalidateQueries({ queryKey: ["machines"] });
    } catch (err) {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("保存失败", { description: msg });
      throw err;
    }
  }

  return (
    <div className="space-y-4">
      <form
        onSubmit={apply}
        aria-label="机器过滤器"
        className="grid grid-cols-1 gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 sm:grid-cols-4"
      >
        <label className="block text-xs">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            status
          </span>
          <Select
            value={form.status ?? "all"}
            onValueChange={(value) =>
              setForm({ ...form, status: value === "all" ? undefined : value })
            }
          >
            <SelectTrigger aria-label="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUTH_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="block text-xs sm:col-span-2">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            搜索
          </span>
          <Input
            aria-label="q"
            placeholder="hostname / mac_id 子串"
            value={form.q ?? ""}
            onChange={(e) => setForm({ ...form, q: e.target.value || undefined })}
          />
        </label>
        <div className="flex items-end gap-2">
          <Button type="submit" size="sm">
            应用
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reset}>
            重置
          </Button>
        </div>
      </form>

      <QueryView query={q} isEmpty={(d) => d.items.length === 0}>
        {(data) => (
          <>
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>主机名</TableHead>
                  <TableHead>mac_id</TableHead>
                  <TableHead>agent_id</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>备注</TableHead>
                  <TableHead>标签</TableHead>
                  <TableHead>最后出现</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row: MachineListEntry) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono">
                      <Link
                        to="/machines/$machineId"
                        params={{ machineId: String(row.subject_id) }}
                        className="hover:underline"
                      >
                        {row.subject_id}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono">
                      <Link
                        to="/machines/$machineId"
                        params={{ machineId: String(row.subject_id) }}
                        className="hover:underline"
                      >
                        {row.subject}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono">{row.mac_id}</TableCell>
                    <TableCell className="font-mono">
                      {row.agent_id ?? ""}
                    </TableCell>
                    <TableCell>
                      <StatusBadge variant={statusToVariant(row.status)}>
                        {row.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <InlineEditableCell
                        value={row.note ?? ""}
                        disabled={!canWrite}
                        ariaLabel={`note-${row.id}`}
                        onSave={(next) =>
                          saveMetadata(row.id, next, row.tags ?? "")
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <InlineEditableCell
                        value={row.tags ?? ""}
                        disabled={!canWrite}
                        ariaLabel={`tags-${row.id}`}
                        onSave={(next) =>
                          saveMetadata(row.id, row.note ?? "", next)
                        }
                      />
                    </TableCell>
                    <TableCell className="text-[hsl(var(--muted-foreground))]">
                      {row.last_seen_at ? formatMs(row.last_seen_at) : ""}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <AuthActionsCell
                          domain="machine"
                          authID={row.id}
                          status={row.status}
                          subject={row.subject}
                          actor={actor}
                          canWrite={canWrite}
                          onChanged={() =>
                            void queryClient.invalidateQueries({
                              queryKey: ["machines"],
                            })
                          }
                        />
                        <AuthDeleteButton
                          domain="machine"
                          authID={row.id}
                          subject={row.subject}
                          actor={actor}
                          canWrite={canWrite}
                          onDeleted={() =>
                            void queryClient.invalidateQueries({
                              queryKey: ["machines"],
                            })
                          }
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.next_cursor ? (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onChange({ ...search, cursor: data.next_cursor })}
                >
                  下一页
                </Button>
              </div>
            ) : null}
          </>
        )}
      </QueryView>
    </div>
  );
}
